import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useToast,
  ChakraProps,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import {
  MAX_FRACTION_DIGITS,
  WrapBridgeMappings,
  isNumber,
  toFixedWithoutRounding,
} from "@constants/common";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";
import useSwapTokensBalances from "@hooks/useSwapTokensBalances";

import React, { useCallback, useEffect, useState } from "react";
import { formatNumber, isErrorRejectedByUser, trackEvent } from "utils";
import { formatUnits, parseEther } from "viem";
import {
  useAccount,
  useBalance,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import {
  APPROVE_WRAPPING_CLICK,
  CONNECT_WALLET,
  MAX_WRAP_CLICK,
} from "@constants/analytics";
import { useQueryClient } from "@tanstack/react-query";
import TicketInputSm from "@components/TicketInputSm";
import UTMLink from "@components/UTMLink";
import { FaRegCircleCheck } from "react-icons/fa6";
import { swappingContractsConfig } from "@config/contractsConfig";
import { TChainSelection } from "./types";
import { MAX_UNWRAP_CLICK } from "../../constants/analytics";
import { useDispatch } from "react-redux";
import { updateUnwrapTxnState } from "store/slices/globalSlice";

type TTicketInputRightSectionProps = {
  token: string;
  icon: string;
};
type TTicketInputBalanceProps = {
  onClick(): void;
  token: string;
  tokenAmount: number;
  isConnected: boolean;
  hideMax?: boolean;
  style?: ChakraProps;
};

const txnInitialState = {
  open: false,
  hash: "",
  error: "",
  hasUserDenied: false,
};

const TicketInputRightSection = ({
  token,
  icon,
}: TTicketInputRightSectionProps) => {
  return (
    <Flex flexDir="column" alignItems="center">
      <Flex gap={2} alignItems="center">
        {icon && <BaseImage width={18} height={18} alt={token} src={icon} />}
        <Flex alignItems="center" justify="space-between" flex={1} gap={1}>
          <Box flex={1} fontWeight={500}>
            {token}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

const TicketInputBalance = ({
  token,
  tokenAmount,
  isConnected,
  onClick,
  hideMax = false,
  style,
}: TTicketInputBalanceProps) => {
  return (
    <Flex
      alignItems="baseline"
      justifyContent={"end"}
      gap="6px"
      mt="4px"
      {...style}
    >
      {isConnected && tokenAmount !== undefined && tokenAmount != null ? (
        <Text fontWeight="500" fontSize="12px" mt="6px" color="textPrimary">
          Balance:{" "}
          <strong>
            {formatNumber(Number(tokenAmount), false, 4, true)} {token}
          </strong>
        </Text>
      ) : null}
      {!hideMax && (
        <Button
          bg="backgroundDivider"
          color="textPrimary"
          h="1.5rem"
          size="sm"
          p="4px 8px"
          _hover={{
            bg: "backgroundDivider",
          }}
          onClick={() => onClick()}
          w="41px"
          fontWeight="700"
          fontSize="10px"
          borderRadius="8px"
        >
          MAX
        </Button>
      )}
    </Flex>
  );
};

const WrapUnwrapAccordian = ({
  type,
  sourceChain,
  destinationChain,
}: {
  type: string;
  sourceChain: TChainSelection;
  destinationChain: TChainSelection;
}) => {
  const { chain: wagmiChain } = useAccount();
  const queryClient = useQueryClient();
  const { switchChain } = useSwitchChain();
  const { chain } = useDappChain();
  const chainIdParam = type === "wrap" ? destinationChain.id : sourceChain.id;
  const { swapConfig } = useDappConfig();
  const { isConnected } = useAccount();
  const client = usePublicClient();
  const dispatch = useDispatch();

  const { rsETH = null, wrsETH = null } =
    isConnected && chainIdParam && swappingContractsConfig[chainIdParam]?.tokens
      ? swappingContractsConfig[chainIdParam].tokens
      : {};

  const fromToken = type === "wrap" ? rsETH : wrsETH;
  const [swapAmount, setSwapAmount] = useState("");
  const [error, setError] = useState("");
  const [isPending, setPending] = useState(false);
  const [txnCompleted, setTxnCompleted] = useState(false);
  const { writeContract } = useWriteContract();
  const { openConnectModal } = useConnectModal();
  const toast = useToast();

  const hasToSwitch =
    type === "wrap"
      ? destinationChain?.id !== chain.id
      : sourceChain?.id !== chain.id;

  const { data, queryKey } = useBalance({
    token: rsETH?.address,
    address: wrsETH?.address,
    chainId: chainIdParam,
    query: {
      refetchInterval: 10_000,
    },
  });

  const unwrapLimit =
    data && rsETH?.decimals
      ? Number(formatUnits(data.value, rsETH.decimals))
      : 0;

  const rsEthSelected = rsETH?.symbol === fromToken?.symbol;

  const [isTxModalOpen, setIsTxModalOpen] = useState(txnInitialState);
  const {
    rsETHBalance,
    rsETHAllowance,
    wrsETHBalance,
    wrsETHAllowance,
    invalidateCache,
  } = useSwapTokensBalances(chainIdParam);

  const fromTokenBal = formatUnits(
    rsEthSelected ? rsETHBalance : wrsETHBalance,
    18
  );

  const toTokenBal = formatUnits(
    rsEthSelected ? wrsETHBalance : rsETHBalance,
    18
  );

  const tokenAllowance = formatUnits(
    rsEthSelected ? rsETHAllowance : wrsETHAllowance,
    18
  );

  const hasSufficientAllowance =
    !rsEthSelected || Number(tokenAllowance) >= Number(swapAmount);

  const toToken = rsEthSelected ? wrsETH : rsETH;

  const chainIdForSwitch =
    type === "wrap" ? destinationChain.id : sourceChain?.id;

  const hasBalance = Number(toFixedWithoutRounding(fromTokenBal, 4)) > 0;

  const checkInput = useCallback(
    (value = swapAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > Number(fromTokenBal)) {
        error = `You can't wrap/unwrap more than available balance in your wallet.`;
      } else if (!rsEthSelected && Number(value) > Number(unwrapLimit)) {
        error = `You can't unwrap more than available limit of ${unwrapLimit} ${fromToken?.symbol}.`;
      }
      return error;
    },
    [
      swapAmount,
      isConnected,
      fromTokenBal,
      rsEthSelected,
      unwrapLimit,
      fromToken?.symbol,
    ]
  );

  useEffect(() => {
    if (swapAmount !== "") {
      const error = checkInput(swapAmount);
      setError(error);
    }
  }, [checkInput, swapAmount, fromTokenBal]);

  useEffect(() => {
    if (txnCompleted) {
      setTxnCompleted(false);
    }
  }, [chain, sourceChain, destinationChain]);

  const handleApprove = () => {
    if (!client || !fromToken || !wrsETH) {
      return;
    }

    setIsTxModalOpen({
      open: true,
      hash: "",
      error: "",
      hasUserDenied: false,
    });

    setPending(true);

    writeContract(
      {
        address: fromToken.address,
        abi: fromToken.abi,
        functionName: "approve",
        args: [wrsETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          trackEvent(APPROVE_WRAPPING_CLICK, error);
          setPending(false);
          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            // @ts-ignore
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            hasUserDenied: isErrorRejectedByUser(error),
          });
        },
        onSuccess: async (hash) => {
          trackEvent(APPROVE_WRAPPING_CLICK, {
            rsETH_Balance: formatUnits(rsETHBalance, 18),
            wrsETH_Balance: formatUnits(wrsETHBalance, 18),
          });
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
          await client.waitForTransactionReceipt({
            hash,
          });
          invalidateCache();
          queryClient.invalidateQueries({ queryKey });
          setPending(false);
        },
      }
    );
  };

  const handleSwap = () => {
    if (!client || !fromToken || !rsETH || !wrsETH) {
      return;
    }
    const error = checkInput(swapAmount);
    if (error) {
      setError(error);
      return;
    }

    if (wagmiChain?.id !== swapConfig.chain.id) {
      toast({
        description: "chain and config mismatch. Please refresh",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log(`chain and config mismatch. Please refresh`);
      return;
    }

    setIsTxModalOpen({
      open: true,
      hash: "",
      error: "",
      hasUserDenied: false,
    });
    setPending(true);
    const functionName =
      fromToken.symbol === rsETH.symbol ? "deposit" : "withdraw";

    writeContract(
      {
        address: wrsETH.address,
        abi: wrsETH.abi,
        functionName,
        args: [rsETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          setPending(false);
          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            // @ts-ignore
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            hasUserDenied: isErrorRejectedByUser(error),
          });
          setTxnCompleted(true);
        },
        onSuccess: async (hash) => {
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
          await client.waitForTransactionReceipt({
            hash,
          });
          invalidateCache();
          setPending(false);
          queryClient.invalidateQueries({ queryKey });
          setSwapAmount("");
          setTxnCompleted(true);
          if (type !== "wrap") {
            dispatch(
              updateUnwrapTxnState({
                success: true,
              })
            );
          }
        },
      }
    );
  };

  const handleMaxClick = () => {
    const ev = type === "wrap" ? MAX_WRAP_CLICK : MAX_UNWRAP_CLICK;
    trackEvent(ev, {
      rsETH_Balance: formatUnits(rsETHBalance, 18),
      wrsETH_Balance: formatUnits(wrsETHBalance, 18),
    });
    setSwapAmount(toFixedWithoutRounding(fromTokenBal, 4));
  };

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setSwapAmount(val);
      } else {
        if (txnCompleted) setTxnCompleted(false);
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setSwapAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "wrap_page",
    });
  };

  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (hasToSwitch)
      return `Switch to ${
        type === "wrap" ? destinationChain.title : sourceChain.title
      }`;
    if (!hasSufficientAllowance) return isPending ? "Approving" : "Approve";
    if (rsEthSelected) return isPending ? "Wrapping Token" : "Wrap Token";
    return isPending ? "Unwrapping Token" : "Unwrap Token";
  };

  const handleButtonClick = () => {
    if (!isConnected) {
      handleConnectWallet();
    } else if (hasToSwitch) {
      switchChain({ chainId: Number(chainIdForSwitch) });
    } else if (!hasSufficientAllowance) {
      handleApprove();
    } else {
      handleSwap();
    }
  };

  if (!fromToken || !toToken) {
    return null;
  }

  return (
    <>
      <Flex justifyContent="center" p={{ base: "0" }}>
        <Flex
          flexDir="column"
          gap="16px"
          mt={{ base: "16px", xl: "0px" }}
          w={{ base: "100%", md: "648px" }}
        >
          <Flex
            borderRadius="24px"
            bg="backgroundCard"
            p={{ base: "16px 16px 24px", md: "20px" }}
            gap="16px"
            w={"100%"}
            direction={"column"}
          >
            <Flex>
              <Text fontSize={"12px"} lineHeight={"15px"} fontWeight={600}>
                On{" "}
                {type === "wrap" ? destinationChain?.title : sourceChain?.title}
              </Text>
            </Flex>
            <Flex
              justifyContent={"space-between"}
              direction={{ base: "column", sm: "row" }}
            >
              <Flex width={{ base: "100%", sm: "45%" }} direction={"column"}>
                <TicketInputSm
                  inputRightElement={
                    <TicketInputRightSection
                      token={fromToken.symbol}
                      icon={WrapBridgeMappings.ICONS[fromToken.symbol]}
                    />
                  }
                  value={swapAmount}
                  onChange={(value: string) => handleChange(value)}
                  error={error}
                />
                <TicketInputBalance
                  isConnected={isConnected}
                  token={fromToken.symbol}
                  tokenAmount={+toFixedWithoutRounding(fromTokenBal, 4)}
                  onClick={handleMaxClick}
                  // style={{ justifyContent: "space-between" }}
                />
              </Flex>
              <Flex
                mt={{ base: "10px", sm: "-20px" }}
                mb={{ base: "10px", sm: 0 }}
                justifyContent={"center"}
                transform={{ base: "rotate(90deg)", sm: "none" }}
              >
                <BaseImage
                  src="/assets/arrow-right.svg"
                  alt="swap"
                  height={24}
                  width={23}
                  loading="eager"
                />
              </Flex>
              <Flex width={{ base: "100%", sm: "45%" }} direction={"column"}>
                <TicketInputSm
                  inputRightElement={
                    <TicketInputRightSection
                      token={toToken.symbol}
                      icon={WrapBridgeMappings.ICONS[toToken.symbol]}
                    />
                  }
                  value={swapAmount}
                  disabled
                />
                <TicketInputBalance
                  isConnected={isConnected}
                  token={toToken.symbol}
                  tokenAmount={+toFixedWithoutRounding(toTokenBal, 4)}
                  onClick={handleMaxClick}
                  hideMax
                />
              </Flex>
            </Flex>

            {!rsEthSelected && (
              <Flex justify="start" gap="1" width="100%">
                <Flex gap={1} alignItems={"center"}>
                  <Text fontSize={"10px"} lineHeight={"15px"} fontWeight={400}>
                    Unwrap Limit:
                  </Text>
                </Flex>
                <Text fontSize={"10px"} lineHeight={"15px"} fontWeight={700}>
                  {+unwrapLimit.toFixed(4)}
                </Text>
              </Flex>
            )}

            {!txnCompleted ? (
              <Button
                borderRadius="6px"
                bg={"textPrimary"}
                p="20px 24px 20px 24px"
                h="32px"
                minW="126px"
                _hover={{
                  bg: "textPrimary",
                }}
                isDisabled={
                  !!error || isPending || (!hasToSwitch && !swapAmount)
                }
                onClick={handleButtonClick}
              >
                {isPending && <Spinner size="sm" color="white" />}
                <Text
                  fontSize={"14px"}
                  lineHeight={"18px"}
                  fontWeight={500}
                  color="backgroundCard"
                  ml="6px"
                >
                  {getButtonText()}
                </Text>
              </Button>
            ) : (
              <>
                <Button
                  borderRadius="6px"
                  bg={isTxModalOpen.error ? "#FF4D4D" : "#07A658"}
                  p="20px 24px 20px 24px"
                  h="32px"
                  minW="126px"
                  _hover={{
                    bg: isTxModalOpen.error ? "#FF4D4D" : "#07A658",
                  }}
                  cursor={"default"}
                >
                  {!isTxModalOpen.error && <FaRegCircleCheck color="#fff" />}
                  <Text
                    fontSize={"14px"}
                    lineHeight={"18px"}
                    fontWeight={500}
                    color="backgroundCard"
                    ml="6px"
                  >
                    {isTxModalOpen.error
                      ? "TRANSACTION FAILED"
                      : rsEthSelected
                      ? "WRAPPED SUCCESSFULLY"
                      : "UNWRAPPED SUCCESSFULLY"}
                  </Text>
                </Button>

                <Flex
                  fontSize="10px"
                  lineHeight="15px"
                  fontWeight="400"
                  textAlign="center"
                  color={isTxModalOpen.error ? "#FF616A" : "textSecondary"}
                  justifyContent={"center"}
                >
                  {isTxModalOpen.error ? (
                    <Flex gap={1}>
                      <Text
                        borderBottom="1px solid"
                        borderColor="#FF616A"
                        onClick={() => {
                          setIsTxModalOpen(txnInitialState);
                          setTxnCompleted(false);
                        }}
                        cursor={"pointer"}
                      >
                        Click here
                      </Text>
                      <Text> to reset & try again</Text>
                    </Flex>
                  ) : (
                    <UTMLink
                      isExternal
                      borderBottom="1px solid"
                      borderColor="textSecondary"
                      onClick={() => {
                        const url = `${chain.blockExplorers?.default.url}/tx/${isTxModalOpen.hash}`;
                        window.open(url, "_blank");
                      }}
                    >
                      View Transaction
                    </UTMLink>
                  )}
                </Flex>
              </>
            )}

            {error && !txnCompleted && (
              <Flex alignItems="center" justifyContent={"center"}>
                <BaseImage
                  src="/assets/transactions/error.svg"
                  width={20}
                  height={20}
                  alt="error-icon"
                />
                <Text
                  ml="0.5rem"
                  color="error"
                  fontSize="12px"
                  fontWeight="400"
                >
                  {error ? error : "error"}
                </Text>
              </Flex>
            )}

            {!hasBalance && !error && !txnCompleted && (
              <Flex alignItems="center" justifyContent={"center"}>
                <BaseImage
                  src="/assets/transactions/error.svg"
                  width={20}
                  height={20}
                  alt="error-icon"
                />
                <Text
                  ml="0.5rem"
                  color="error"
                  fontSize="12px"
                  fontWeight="400"
                >
                  Not enough balance for this transaction
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default WrapUnwrapAccordian;
